import { createTRPCClient, httpBatchLink, httpLink, TRPCClientError } from '@trpc/client';
import type { AppRouter } from '../../../api/src/trpc/router.js';
import { apiBaseUrl, apiConfigured } from './api-url.js';
import { currentAccessToken, sessionIsFresh } from './auth.js';
import { ledger } from './browser.js';

/**
 * The typed API client.
 *
 * TYPE-ONLY IMPORT ACROSS THE WORKSPACE. `AppRouter` is imported with `import type`, so
 * it is erased at build time and no API runtime code is pulled into the browser bundle.
 * If that import ever loses its `type` keyword, Vite would try to bundle Fastify and pg
 * for the browser — noisy rather than silent, but worth knowing why the line looks odd.
 *
 * THE TOKEN IS FETCHED IN `headers()`, WHICH RUNS PER REQUEST. Capturing it once would
 * hold a value with an expiry, and the resulting failure is not a clean 401: with RLS
 * a stale token yields an empty 200. Freshness is not a nicety here, it is the
 * difference between "no data" and "not you any more".
 */

// Hoisted to `api-url.ts` in 0.1.209 — this was one of four copies. Re-exported because
// `apiConfigured` is imported from HERE across the app, and moving a name is a rename.
const API_URL = apiBaseUrl();

export { apiConfigured };

export const api = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: `${API_URL ?? ''}/trpc`,
      /**
       * SPLIT A BATCH BEFORE THE URL OUTGROWS NODE'S HEADER LIMIT (0.1.471). Batched queries
       * travel as ONE GET with every input in the query string, and Node refuses request
       * headers over 16 KB (a 431, `--max-http-header-size`). The projection's thumbnail
       * signing fires ~16 `photoUrls` chunks of a hundred keys in one tick (items.tsx); merged
       * into one URL that is ~50 KB and never reaches the router. 8,000 leaves room for the
       * bearer token and the other headers: a batch that would exceed it goes as several
       * PARALLEL requests, and a single call larger than the limit is sent alone, exactly as
       * it is today.
       */
      maxURLLength: 8_000,
      async headers() {
        const token = await currentAccessToken();
        return token ? { authorization: `Bearer ${token}` } : {};
      },
    }),
  ],
});

/**
 * THE SAME API, ONE CALL PER REQUEST — for the read that must NOT join the boot batch (0.1.481).
 * `httpBatchLink` folds every call made in one tick into one request, and a batch answers when
 * its slowest member does: the nav counts (eight screens' reads, 504 ms in production on 7 Sep
 * 2026) were riding in the dashboard's batch and holding the landing screen's paint until they
 * were done. Through this client a call is its own request, in parallel with the batch, and the
 * screen paints when ITS reads return. Same URL, same token, same error shapes; only for calls
 * whose answer the current screen is not waiting on — today, `navCounts` alone.
 */
export const apiUnbatched = createTRPCClient<AppRouter>({
  links: [
    httpLink({
      url: `${API_URL ?? ''}/trpc`,
      async headers() {
        const token = await currentAccessToken();
        return token ? { authorization: `Bearer ${token}` } : {};
      },
    }),
  ],
});

export interface CallFailure {
  kind: 'unauthenticated' | 'stale-session' | 'unavailable' | 'error';
  message: string;
}

/**
 * Wraps a call so that every failure produces a ledger entry and a user-facing message,
 * and so that an authorisation failure is distinguished from a transport failure.
 *
 * The `stale-session` case exists because a 401 after a long idle period is not the same
 * event as a 401 on a fresh session: the first wants a silent re-auth prompt, the second
 * is a real permission problem. Guessing between them from the status code alone is how
 * you end up signing people out of a working session.
 *
 * WHOSE SENTENCE THE USER READS — found 27 Jul by an e2e that expected the server's own
 * words and got the classifier's. Every refusal this project writes with care ("this
 * item appears in bookings or movement history and cannot be deleted — retire it
 * instead", "has sub-categories — delete or move those first", "already exists") was
 * being replaced by a generic line: a 409 became "Someone else changed this first",
 * which is not what happened, and a 400 became "Support has been notified", which is
 * both wrong and alarming. The rule now:
 *
 *   4xx — the server is talking ABOUT THIS REQUEST and wrote a sentence for a human.
 *         Show it. It knows what the classifier cannot.
 *   5xx / transport — the failure is OURS, and the classifier's message is right
 *         because it carries the retry semantics the user actually needs.
 *
 * The LEDGER classification is unchanged either way: the code and the retryability
 * still come from the classifier, so nothing about diagnosis moves.
 */
const AUTH_STATUSES = new Set([401, 403]);

/** A 4xx carries a deliberate, user-facing sentence from our own router. */
function serverSentence(error: unknown, status: number | undefined): string | undefined {
  if (status === undefined || !(error instanceof TRPCClientError)) return undefined;
  /**
   * ⚠️ A REFUSED SEND IS NOT "WILL BE RETRIED" (0.1.519). Our router answers BAD_GATEWAY on
   * exactly one path: a mail the provider — or our own attachment ceiling — refused, AFTER
   * the failure is recorded on the notification (the four mail sends in router.ts). Nothing
   * retries a send a person pressed (maintainer, 1 Oct 2026: "no" to automatic re-sends —
   * a client could get it twice), yet the classifier's 502 sentence promised a retry and
   * threw away the reason. "KNOKKE OFF" (27 garments) read "The server had a problem. It
   * will be retried." when the server had said "the packing list is 46.0 MB, over the 15.0 MB
   * limit". A proxy's bare 502 carries no tRPC `code`, so it still gets the generic line.
   */
  if (status === 502 && (error.data as { code?: unknown } | null)?.code === 'BAD_GATEWAY') {
    const reason = error.message.trim();
    if (reason !== '') return `Not sent: ${reason}. Nothing was emailed, and nothing will retry it — press Send again once that is fixed.`;
  }
  if (status < 400 || status >= 500) return undefined;
  const message = error.message.trim();
  return message === '' ? undefined : message;
}
export async function call<T>(
  label: string,
  fn: () => Promise<T>,
): Promise<{ ok: true; data: T } | { ok: false; failure: CallFailure }> {
  if (!apiConfigured()) {
    return {
      ok: false,
      failure: { kind: 'unavailable', message: 'This build has no API configured.' },
    };
  }

  try {
    return { ok: true, data: await fn() };
  } catch (error) {
    const status =
      error instanceof TRPCClientError ? (error.data as { httpStatus?: number } | null)?.httpStatus : undefined;

    // `status !== undefined` first so TypeScript keeps the narrowing the ledger call
    // below depends on — a Set lookup alone widens it back to `number | undefined`.
    if (status !== undefined && AUTH_STATUSES.has(status)) {
      const fresh = await sessionIsFresh();
      /**
       * ⚠️ A FRESH 401 IS `unauthenticated`, AND UNTIL 0.1.217 NOTHING EVER PRODUCED THAT KIND.
       * The union member existed, `app.tsx`'s no-access gate listened for it, everything
       * typechecked — and a fresh 401 was classified `error`, so the gate was dead code. Found
       * in production on 10 Aug 2026: an invitee who signed in without redeeming saw "no user
       * row for this subject" splattered across every screen instead of the screen built for
       * exactly that state.
       *
       * The split is by STATUS, which here is the honest signal: the API answers **401** when
       * it refuses the SUBJECT (`unknown_user`, `user_disabled` — you are not somebody who can
       * be here) and **403** when it refuses the ACTION ("Deleting items is for
       * administrators" — you are somebody, you may not do this). A fresh 403 stays `error`
       * with the server's own sentence, because that person is using the app and just bumped a
       * permission wall.
       */
      const failure: CallFailure = !fresh
        ? { kind: 'stale-session', message: 'Your session expired. Please sign in again.' }
        : status === 401
          ? { kind: 'unauthenticated', message: serverSentence(error, status) ?? 'This account has no access.' }
          : { kind: 'error', message: serverSentence(error, status) ?? 'You do not have permission to do that.' };
      ledger.record(error, `api:${label}`, {
        notified: true,
        // The status goes to the CLASSIFIER, not only into the context. Passing it as
        // context alone is what produced `UNKNOWN (api:whoami, default)` on the first
        // authenticated request this app ever made.
        classify: { httpStatus: status },
        context: { httpStatus: status, sessionFresh: fresh },
      });
      return { ok: false, failure };
    }

    const entry = ledger.record(error, `api:${label}`, {
      notified: true,
      ...(status !== undefined ? { classify: { httpStatus: status } } : {}),
      ...(status !== undefined ? { context: { httpStatus: status } } : {}),
    });
    return {
      ok: false,
      failure: {
        kind: status === undefined ? 'unavailable' : 'error',
        message: serverSentence(error, status) ?? entry.userMessage,
      },
    };
  }
}
